import React from "react";
import Image from "next/image";
import {
  getImageUrl,
  isValidImageUrl,
  getPlaceholderImage,
} from "@/utils/image";

export interface NewsCardProps {
  /**
   * URL của hình ảnh
   */
  imageUrl: string;

  /**
   * Alt text cho hình ảnh
   */
  imageAlt: string;

  /**
   * Tiêu đề của tin tức
   */
  title: string;

  /**
   * Mô tả chi tiết, hỗ trợ HTML
   */
  description: string;

  /**
   * Class name tùy chỉnh cho container
   */
  className?: string;

  /**
   * Hàm callback khi click vào card
   */
  onClick?: () => void;
}

const NewsCard: React.FC<NewsCardProps> = ({
  imageUrl,
  imageAlt,
  title,
  description,
  className = "",
  onClick,
}) => {
  // Get the processed image URL
  const processedImageUrl = getImageUrl(imageUrl);

  // Determine the final image source
  const imageSrc = isValidImageUrl(processedImageUrl)
    ? processedImageUrl
    : getPlaceholderImage(400, 225, "No Image");

  return (
    <div
      className={`
        bg-white rounded-lg shadow-md overflow-hidden
        hover:shadow-lg transition-shadow duration-300
        cursor-pointer group
        ${className}
      `}
      onClick={onClick}
    >
      {/* Image container */}
      <div className="relative aspect-video overflow-hidden">
        <Image
          src={imageSrc}
          alt={imageAlt || "News image"}
          fill
          className="object-cover group-hover:scale-105 transition-transform duration-300"
          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
        />
      </div>

      {/* Content container */}
      <div className="p-4">
        {/* Title */}
        <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2 group-hover:text-blue-600 transition-colors duration-200">
          {title}
        </h3>

        {/* Description with HTML support */}
        <div
          className="text-gray-600 text-sm leading-relaxed line-clamp-3"
          dangerouslySetInnerHTML={{ __html: description }}
        />
      </div>
    </div>
  );
};

export default NewsCard;
